import { FilterSortPageType, GenericColumnModel, InteractionManager } from "../BaseTable/models";
import ErrorBoundary from "features/ErrorBoundary";
import { LocalTable } from "../LocalTable/LocalTable";
import { useCallback, useEffect, useState } from "react";
import { collapseAllRows, getCollapsedRowsData, getExpandedRowsData, getInitialRowsData, handleExpandRows } from "./helpers";
import { HeaderGroup } from "../BaseTable/Header/Header";

export type NestedTableProps = {
	columns: GenericColumnModel[]
	rowsData: any[]
	filterSortPage: FilterSortPageType
	fetching?: boolean
	hasPagination?: boolean
	compact?: boolean
	fill?: boolean
	interactionManager: InteractionManager
	level: number | undefined
	disabledReorder?: boolean
	additionalHeaderRow?: HeaderGroup[]
	isWidgetData?: boolean
	onGroupClick?: (groupId: string) => void
	groupByLevels?: boolean
}

export const NestedTable = ({
	columns,
	rowsData,
	filterSortPage,
	fetching,
	hasPagination,
	compact,
	fill,
	interactionManager,
	level,
	disabledReorder,
	additionalHeaderRow,
	isWidgetData = false,
	onGroupClick,
	groupByLevels = false
}: NestedTableProps) => {
	const [nestedRowsData, setNestedRowsData] = useState<any[]>([]);

	useEffect(
		() => {
			let updatedRows = getInitialRowsData(rowsData, isWidgetData, groupByLevels);

			if (level !== undefined) {
				updatedRows = handleExpandRows(updatedRows, rowsData, level);
			}

			setNestedRowsData(updatedRows);
		},
		[rowsData, isWidgetData, groupByLevels, level]
	)

	useEffect(
		() => {
			setNestedRowsData((state) => {
				const updatedRows = collapseAllRows(state);
				return updatedRows;
			});
		},
		[filterSortPage.offset]
	)

	const onExpandCallback = useCallback(
		(expandedItem: any) => {
			if (filterSortPage.nestedLimit) {
				const currentLimit = filterSortPage.nestedLimit;
				const childrenCount = expandedItem.children?.length || 0;
				const newLimit = currentLimit + childrenCount;
				interactionManager.changeNestedLimit?.(newLimit);
			}

			const updatedRowsData = getExpandedRowsData(nestedRowsData, rowsData, expandedItem, isWidgetData, groupByLevels);
			setNestedRowsData(updatedRowsData);
		},
		[rowsData, nestedRowsData, isWidgetData, filterSortPage.nestedLimit, interactionManager, groupByLevels]
	)

	const onCollapseCallback = useCallback(
		(collapsedItem: any) => {
			if (filterSortPage.nestedLimit) {
				const currentLimit = filterSortPage.nestedLimit;
				const childrenCount = collapsedItem.children?.length || 0;
				const newLimit = currentLimit - childrenCount;
				interactionManager.changeNestedLimit?.(newLimit);
			}

			const updatedRowsData = getCollapsedRowsData(nestedRowsData, collapsedItem, isWidgetData, groupByLevels);
			setNestedRowsData(updatedRowsData);
		},
		[nestedRowsData, isWidgetData, filterSortPage.nestedLimit, interactionManager, groupByLevels]
	)

	return (
		<ErrorBoundary location='NestedTable'>
			<LocalTable
				columns={columns}
				rowsData={nestedRowsData}
				filterSortPage={filterSortPage}
				fetching={fetching}
				hasPagination={hasPagination}
				compact={compact}
				fill={fill}
				interactionManager={interactionManager}
				disabledReorder={disabledReorder}
				additionalHeaderRow={additionalHeaderRow}
				expand={onExpandCallback}
				collapse={onCollapseCallback}
				onGroupClick={onGroupClick}
				isNestedRowsData
			/>
		</ErrorBoundary>
	)
}
